import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Search, SearchX, Archive, FileText } from "lucide-react";
import { api, formatDate } from "@/lib/api";
import { PageBanner, Pagination, PILLARS, Txt, SectionHeading } from "@/components/site/Brand";
import { PublicationCard, DownloadButton } from "@/components/site/Cards";
import NotFound from "./NotFound";

function Filters({ params, setParams, tax, years }) {
  const [q, setQ] = useState(params.get("q") || "");
  useEffect(() => setQ(params.get("q") || ""), [params]);
  const update = (k, v) => {
    const n = new URLSearchParams(params);
    v ? n.set(k, v) : n.delete(k);
    n.delete("page");
    setParams(n);
  };
  const sel = (k, label, opts) => (
    <div>
      <label htmlFor={`filter-${k}`} className="field-label">{label}</label>
      <select id={`filter-${k}`} className="field-input" value={params.get(k) || ""} onChange={(e) => update(k, e.target.value)} data-testid={`filter-${k}`}>
        <option value="">All</option>
        {opts.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
  return (
    <div className="rounded-xl bg-irid-mist p-6 md:p-8" data-testid="publication-filters">
      <form role="search" onSubmit={(e) => { e.preventDefault(); update("q", q.trim()); }} className="mb-6">
        <label htmlFor="pub-search" className="field-label">Search publications</label>
        <div className="flex gap-3">
          <input id="pub-search" type="search" className="field-input" placeholder="Search by title, abstract or keyword" value={q} onChange={(e) => setQ(e.target.value)} data-testid="publication-search-input" />
          <button type="submit" className="btn-primary" data-testid="publication-search-submit"><Search aria-hidden="true" className="h-4 w-4" /><span className="hidden sm:inline">Search</span></button>
        </div>
      </form>
      <div className="grid gap-5 md:grid-cols-3">
        {sel("category", "Category", tax.publication_categories || [])}
        {sel("document_type", "Document type", tax.document_types || [])}
        {sel("year", "Year", years.map(String))}
      </div>
    </div>
  );
}

export function Publications() {
  const [params, setParams] = useSearchParams();
  const [tax, setTax] = useState({});
  const [data, setData] = useState(null);
  useEffect(() => { document.title = "Research library – IRID"; api.get("/taxonomies").then((r) => setTax(r.data)); }, []);
  useEffect(() => {
    let live = true;
    api.get("/publications", { params: Object.fromEntries(params) })
      .then((r) => live && setData(r.data))
      .catch(() => live && setData({ items: [], total: 0, pages: 1, page: 1, years: [] }));
    return () => { live = false; };
  }, [params]);
  const hasFilters = ["q", "category", "document_type", "year"].some((k) => params.get(k));
  return (
    <>
      <PageBanner title="Research library" eyebrow="Research and publications" intro="Browse, search and download IRID publications approved for public release." crumbs={[{ to: "/", label: "Home" }, { label: "Research" }]} accent="bg-irid-teal" testid="publications-banner" />
      <section className="bg-white py-14 md:py-20">
        <div className="container-irid">
          <Filters params={params} setParams={setParams} tax={tax} years={data?.years || []} />
          <p className="mt-8 text-irid-slate" role="status" aria-live="polite" data-testid="publication-result-count">
            {data ? `${data.total} publication${data.total === 1 ? "" : "s"} found` : "Loading publications…"}
          </p>
          {data && data.items.length === 0 ? (
            <div className="mt-8 flex flex-col items-start gap-4 rounded-xl border border-dashed border-irid-cloud p-10" data-testid="publication-empty-state">
              <SearchX aria-hidden="true" className="h-10 w-10 text-irid-teal" />
              <h2 className="text-2xl">No publications match your search</h2>
              <p className="text-irid-slate">Try a different keyword, or clear the filters to see all publications.</p>
              {hasFilters && <button type="button" className="btn-secondary" onClick={() => setParams({})} data-testid="publication-clear-filters">Clear filters</button>}
            </div>
          ) : (
            <div className="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="publication-results">
              {data?.items.map((p, i) => <PublicationCard key={p.slug} pub={p} index={i} />)}
            </div>
          )}
          {data && <Pagination page={data.page} pages={data.pages} onPage={(pg) => { const n = new URLSearchParams(params); n.set("page", pg); setParams(n); window.scrollTo(0, 0); }} testid="publication-pagination" />}
        </div>
      </section>
    </>
  );
}

export function PublicationDetail() {
  const { slug } = useParams();
  const [p, setP] = useState(undefined);
  useEffect(() => {
    let live = true;
    setP(undefined);
    api.get(`/publications/${slug}`).then((r) => { if (live) { setP(r.data); document.title = "Publication – IRID"; } }).catch(() => live && setP(null));
    return () => { live = false; };
  }, [slug]);
  if (p === null) return <NotFound />;
  if (!p) return <div className="min-h-[60vh]" aria-busy="true" />;
  const svc = p.related_service_obj;
  const sp = svc ? PILLARS[svc.pillar] : null;
  const meta = [
    ["Document type", p.document_type], ["Publication date", formatDate(p.publication_date)], ["Year", p.publication_year],
    ["Author", p.author_name], ["Subject categories", (p.categories || []).join(", ")], ["Keywords", (p.keywords || []).join(", ")],
  ];
  return (
    <>
      <PageBanner title={<Txt>{p.title}</Txt>} eyebrow={p.document_type} accent="bg-irid-teal"
        crumbs={[{ to: "/", label: "Home" }, { to: "/publications", label: "Research" }, { label: "Publication" }]} testid="publication-banner">
        <p className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-white/90" data-testid="publication-byline">
          <span>{p.document_type}</span><span>{p.publication_year}</span><Txt>{p.author_name}</Txt>
        </p>
      </PageBanner>
      {p.status === "archived" && (
        <div className="border-b border-irid-orange-deep/30 bg-irid-gold/10" data-testid="publication-archived-notice">
          <div className="container-irid flex items-center gap-3 py-4 text-irid-orange-deep"><Archive aria-hidden="true" className="h-5 w-5" /><strong>Archived publication.</strong> This record is retained for reference and may be outdated.</div>
        </div>
      )}
      <section className="bg-white py-16 md:py-20">
        <div className="container-irid grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <h2 className="text-2xl md:text-3xl">Abstract</h2>
            <div className="growth-bar mt-3" aria-hidden="true" />
            <Txt as="p" className="mt-6 max-w-[70ch] text-lg">{p.abstract}</Txt>
            <h2 className="mt-14 text-2xl">Details</h2>
            <dl className="mt-6 divide-y divide-irid-cloud rounded-xl border border-irid-cloud" data-testid="publication-metadata">
              {meta.map(([k, v]) => (
                <div key={k} className="grid gap-1 px-5 py-4 sm:grid-cols-3"><dt className="font-semibold text-irid-navy">{k}</dt><dd className="sm:col-span-2"><Txt>{v ? String(v) : "—"}</Txt></dd></div>
              ))}
              {svc && <div className="grid gap-1 px-5 py-4 sm:grid-cols-3"><dt className="font-semibold text-irid-navy">Related service</dt><dd className="sm:col-span-2"><Link to={`/services/${svc.slug}`} className={`irid-link`}>{svc.title}</Link></dd></div>}
            </dl>
          </div>
          <aside className="lg:col-span-4">
            <div className="card-irid card-top-growth sticky top-28 p-7 pt-8" data-testid="publication-download-panel">
              <FileText aria-hidden="true" className="h-8 w-8 text-irid-teal" />
              <h2 className="mt-4 text-xl">Download publication</h2>
              <p className="mt-2 text-sm text-irid-slate">Format: PDF{p.file_name ? ` · ${p.file_name}` : ""}</p>
              <DownloadButton pub={p} className="mt-6 w-full" />
              {sp && <p className={`mt-6 flex items-center gap-2 text-sm font-semibold ${sp.text}`}><sp.Icon aria-hidden="true" className="h-4 w-4" />{svc.title}</p>}
            </div>
          </aside>
        </div>
      </section>
      {p.related.length > 0 && (
        <section className="bg-irid-mist py-20" data-testid="publication-related">
          <div className="container-irid">
            <SectionHeading eyebrow="Keep reading" title="Related publications" />
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">{p.related.map((x, i) => <PublicationCard key={x.slug} pub={x} index={i} />)}</div>
          </div>
        </section>
      )}
    </>
  );
}
